import { useState } from 'react';
import { X, Send, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOTIVOS = [
  'Compra de propiedad',
  'Venta de propiedad',
  'Renta de propiedad',
  'Asesoría de inversión',
  'Valuación',
  'Consulta general',
  'Otro'
];

export const ContactModal = ({ isOpen, onClose, professional }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    nombre_cliente: '',
    email: '',
    telefono: '',
    motivo: '',
    ciudad_operacion: '',
    mensaje: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API}/contact-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          professional_id: professional.id
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al enviar la solicitud');
      }

      setIsSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      nombre_cliente: '',
      email: '',
      telefono: '',
      motivo: '',
      ciudad_operacion: '',
      mensaje: ''
    });
    setIsSuccess(false);
    setError(null);
    onClose();
  };

  if (!professional) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="contact-modal">
        <DialogHeader>
          <DialogTitle className="font-manrope text-xl">
            Solicitar Contacto
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Envía una solicitud de contacto a {professional.nombre}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-8 text-center" data-testid="contact-success">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Send className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-manrope font-semibold text-lg text-slate-900 mb-2">
              Solicitud Enviada
            </h3>
            <p className="text-slate-600 text-sm mb-6">
              Tu solicitud ha sido enviada correctamente. El profesional se pondrá en contacto contigo.
            </p>
            <Button onClick={handleClose} className="bg-slate-900 hover:bg-slate-800">
              Cerrar
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Notice */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
              Esta solicitud se realiza dentro de una red profesional verificada. 
              Sus datos serán tratados conforme a nuestro aviso de privacidad.
            </div>

            {/* Name */}
            <div>
              <Label htmlFor="nombre" className="text-sm font-medium text-slate-700">
                Nombre completo *
              </Label>
              <Input
                id="nombre"
                type="text"
                required
                value={formData.nombre_cliente}
                onChange={(e) => handleChange('nombre_cliente', e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="Tu nombre"
                data-testid="contact-nombre-input"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Correo electrónico *
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="tu@email.com"
                data-testid="contact-email-input"
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="telefono" className="text-sm font-medium text-slate-700">
                Teléfono
              </Label>
              <Input
                id="telefono"
                type="tel"
                value={formData.telefono}
                onChange={(e) => handleChange('telefono', e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="+52 55 1234 5678"
                data-testid="contact-telefono-input"
              />
            </div>

            {/* Motivo */}
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Motivo de contacto *
              </Label>
              <Select
                value={formData.motivo}
                onValueChange={(value) => handleChange('motivo', value)}
                required
              >
                <SelectTrigger className="mt-1 border-slate-200" data-testid="contact-motivo-select">
                  <SelectValue placeholder="Selecciona un motivo" />
                </SelectTrigger>
                <SelectContent>
                  {MOTIVOS.map((motivo) => (
                    <SelectItem key={motivo} value={motivo}>
                      {motivo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Ciudad */}
            <div>
              <Label htmlFor="ciudad" className="text-sm font-medium text-slate-700">
                Ciudad/Zona de interés
              </Label>
              <Input
                id="ciudad"
                type="text"
                value={formData.ciudad_operacion}
                onChange={(e) => handleChange('ciudad_operacion', e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="Ej: Polanco, CDMX"
                data-testid="contact-ciudad-input"
              />
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="mensaje" className="text-sm font-medium text-slate-700">
                Mensaje adicional
              </Label>
              <Textarea
                id="mensaje"
                value={formData.mensaje}
                onChange={(e) => handleChange('mensaje', e.target.value)}
                className="mt-1 border-slate-200 min-h-[80px]"
                placeholder="Describe brevemente tu necesidad..."
                data-testid="contact-mensaje-input"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="contact-error">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 border-slate-200 text-slate-700"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !formData.motivo}
                className="flex-1 bg-slate-900 hover:bg-slate-800"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar Solicitud
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
